<template>
    <div>
      <SettingHeaderTab />
  
      <div class="edit-page">
  
        <!-- Page header -->
        <div class="page-header">
          <div class="page-title-wrap">
            <router-link :to="$url('/all-customers')" class="back-link">
              <font-awesome-icon icon="angle-left" /> Back
            </router-link>
            <div>
              <h5 class="page-title">Edit Customer</h5>
              <p class="page-sub">Update customer details</p>
            </div>
          </div>
          <div class="page-actions">
            <button type="button" class="btn-ghost" @click="$router.go(-1)">
              <font-awesome-icon icon="times" /> Cancel
            </button>
            <button class="btn-primary-action" :disabled="crudLoading || pageLoading" @click="editCustomer">
              <font-awesome-icon v-if="crudLoading" icon="spinner" spin />
              <font-awesome-icon v-else icon="save" />
              {{ crudLoading ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </div>
  
        <!-- Loading skeleton -->
        <div v-if="pageLoading" class="state-block">
          <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
          <p class="mt-3 text-muted-cell">Loading customer...</p>
        </div>
  
        <template v-else>
  
          <!-- Validation errors -->
          <div v-if="hasErrors" class="error-box">
            <div class="error-box-title">
              <font-awesome-icon icon="exclamation-circle" class="me-2" /> Please fix the following errors:
            </div>
            <ul class="error-list">
              <li v-for="(err, i) in flatErrors" :key="i">{{ err }}</li>
            </ul>
          </div>
  
          <form @submit.prevent="editCustomer" class="form-card">
  
            <!-- ── Personal Info ── -->
            <div class="form-section">
              <div class="section-label">Personal Information</div>
              <div class="fields-grid">
  
                <div class="form-group-styled">
                  <label class="form-label-styled">Customer Name <span class="req">*</span></label>
                  <input
                    v-model="customer_name"
                    type="text"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('customer_name') }"
                    placeholder="Enter full name"
                  />
                  <p v-if="fieldError('customer_name')" class="field-error">{{ fieldError('customer_name') }}</p>
                </div>
  
                <div class="form-group-styled">
                  <label class="form-label-styled">Email Address</label>
                  <input
                    v-model="email"
                    type="email"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('email') }"
                    placeholder="Enter email address"
                  />
                  <p v-if="fieldError('email')" class="field-error">{{ fieldError('email') }}</p>
                </div>
  
                <div class="form-group-styled">
                  <label class="form-label-styled">Phone Number</label>
                  <input
                    v-model="phone_no"
                    type="tel"
                    class="form-input-styled"
                    :class="{ 'input--error': fieldError('phone_no') }"
                    placeholder="Enter phone number"
                  />
                  <p v-if="fieldError('phone_no')" class="field-error">{{ fieldError('phone_no') }}</p>
                </div>
  
              </div>
            </div>
  
            <div class="section-divider"></div>
  
            <!-- ── Address ── -->
            <div class="form-section">
              <div class="section-label">Address Details</div>
              <div class="fields-grid">
  
                <div class="form-group-styled" style="grid-column: span 3;">
                  <label class="form-label-styled">Address</label>
                  <input
                    v-model="address"
                    type="text"
                    class="form-input-styled"
                    placeholder="Enter street address"
                  />
                </div>
  
                <div class="form-group-styled">
                  <label class="form-label-styled">Country</label>
                  <select v-model="country_id" class="form-input-styled" @change="getStates">
                    <option value="">Choose country</option>
                    <option v-for="country in countries" :key="country.id" :value="country.id">
                      {{ country.name }}
                    </option>
                  </select>
                </div>
  
                <div class="form-group-styled">
                  <label class="form-label-styled">State</label>
                  <select v-model="state_id" class="form-input-styled" @change="getCities" :disabled="!stateOptions.length">
                    <option value="">Choose state</option>
                    <option v-for="state in stateOptions" :key="state.id" :value="state.id">
                      {{ state.name }}
                    </option>
                  </select>
                </div>
  
                <div class="form-group-styled">
                  <label class="form-label-styled">City</label>
                  <select v-model="city_id" class="form-input-styled" :disabled="!cityOptions.length">
                    <option value="">Choose city</option>
                    <option v-for="city in cityOptions" :key="city.id" :value="city.id">
                      {{ city.name }}
                    </option>
                  </select>
                </div>
  
              </div>
            </div>
  
            <!-- Form footer -->
            <div class="form-footer">
              <button type="button" class="btn-ghost" @click="$router.go(-1)">
                <font-awesome-icon icon="times" /> Cancel
              </button>
              <button type="submit" class="btn-primary-action" :disabled="crudLoading">
                <font-awesome-icon v-if="crudLoading" icon="spinner" spin />
                <font-awesome-icon v-else icon="save" />
                {{ crudLoading ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
  
          </form>
        </template>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, computed, onMounted } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import { useToast } from 'vue-toastification';
  import axios from 'axios';
  import SettingHeaderTab from './components/settingheadertab.vue';
  
  export default {
    name: 'EditCustomer',
    components: { SettingHeaderTab },
  
    setup() {
      const router = useRouter();
      const route  = useRoute();
      const toast  = useToast();
  
      // ── Form fields ──────────────────────────────────────────────────
      const customer_name = ref('');
      const phone_no      = ref('');
      const email         = ref('');
      const address       = ref('');
      const country_id    = ref('');
      const state_id      = ref('');
      const city_id       = ref('');
  
      // ── Lists ────────────────────────────────────────────────────────
      const countries = ref([]);
      const states    = ref([]);   // loaded from API after country change
      const cities    = ref([]);   // loaded from API after state change
  
      // Pre-populated from singleCustomer (may be objects, not arrays)
      const defaultStates = ref([]);
      const defaultCities = ref([]);
  
      // Unified list to render (loaded list takes priority over default)
      const stateOptions = computed(() => states.value.length ? states.value : defaultStates.value);
      const cityOptions  = computed(() => cities.value.length ? cities.value : defaultCities.value);
  
      // ── UI state ─────────────────────────────────────────────────────
      const pageLoading      = ref(false);
      const crudLoading      = ref(false);
      const validationErrors = ref({});
  
      const hasErrors  = computed(() => Object.keys(validationErrors.value).length > 0);
      const flatErrors = computed(() => Object.values(validationErrors.value).flat());
      const fieldError = (field) => validationErrors.value[field]?.[0] ?? null;
  
      // ── Location ─────────────────────────────────────────────────────
      const getStates = async () => {
        if (!country_id.value) return;
        state_id.value = '';
        cities.value   = [];
        city_id.value  = '';
        try {
          const { data } = await axios.get('/states/' + country_id.value);
          states.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      const getCities = async () => {
        if (!state_id.value) return;
        city_id.value = '';
        try {
          const { data } = await axios.get('/cities/' + state_id.value);
          cities.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      const getAllCountries = async () => {
        try {
          const { data } = await axios.get('countries');
          countries.value = data.data.data;
        } catch (err) {
            console.log(err.response?.data?.message);
        }
      };
  
      // ── Load customer ────────────────────────────────────────────────
      const singleCustomer = async () => {
        pageLoading.value = true;
        try {
          const { data } = await axios.get('/settings/customer/' + route.params.id);
          const d = data.data;
          customer_name.value = d.customer_name;
          phone_no.value      = d.phone_no;
          email.value         = d.email;
          address.value       = d.address;
          country_id.value    = d.country_id;
          state_id.value      = d.state_id;
          city_id.value       = d.city_id;
  
          // Pre-populate state/city lists from the customer record
          defaultStates.value = Array.isArray(d.states) ? d.states : (d.state ? [d.state] : []);
          defaultCities.value = Array.isArray(d.cities) ? d.cities : (d.city ? [d.city] : []);
        } catch (_) {
          toast.error('Could not load customer details.');
        } finally {
          pageLoading.value = false;
        }
      };
  
      // ── Submit ───────────────────────────────────────────────────────
      const editCustomer = async () => {
        crudLoading.value = true;
        validationErrors.value = {};
        try {
          const { data } = await axios.put('settings/customer/' + route.params.id, {
            customer_name: customer_name.value,
            phone_no:      phone_no.value,
            email:         email.value,
            address:       address.value,
            country_id:    country_id.value,
            state_id:      state_id.value,
            city_id:       city_id.value,
          });
          toast.success(data.message);
          router.push('/all-customers');
        } catch (err) {
          const status = err.response?.status;
          if (status === 422) {
            toast.warning('Please fix the errors below.');
            validationErrors.value = err.response.data.message;
          } else if (status === 403) {
            toast.error(err.response.data.message);
          } else {
            toast.error('An error occurred. Please try again.');
          }
        } finally {
          crudLoading.value = false;
        }
      };
  
      onMounted(async () => {
        await Promise.all([singleCustomer(), getAllCountries()]);
      });
  
      return {
        customer_name, phone_no, email, address,
        country_id, state_id, city_id,
        countries, stateOptions, cityOptions,
        pageLoading, crudLoading,
        validationErrors, hasErrors, flatErrors, fieldError,
        getStates, getCities, editCustomer,
      };
    },
  };
  </script>
  
  <style scoped>
  /* ── Page ────────────────────────────────────────────────────────────── */
  .edit-page { display:flex; flex-direction:column; gap:16px; margin-top:20px; }
  
  /* ── Header ──────────────────────────────────────────────────────────── */
  .page-header     { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
  .page-title-wrap { display:flex; align-items:flex-start; gap:14px; }
  .page-title      { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
  .page-sub        { font-size:13px; color:#7a90b0; margin:0; }
  .page-actions    { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
  
  .back-link { display:inline-flex; align-items:center; gap:6px; font-size:13px; font-weight:600; color:#7a90b0; padding:6px 10px; border-radius:7px; border:1px solid #e4eaf2; background:#fff; text-decoration:none; transition:all .15s; white-space:nowrap; align-self:flex-start; margin-top:2px; }
  .back-link:hover { background:#f4f7fb; color:#1a2b45; text-decoration:none; }
  
  /* ── Loading state ───────────────────────────────────────────────────── */
  .state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; background:#fff; border:1px solid #e4eaf2; border-radius:12px; }
  .text-muted-cell { color:#4a5e78; }
  .mt-3 { margin-top:12px; }
  .me-2 { margin-right:8px; }
  
  /* ── Error box ───────────────────────────────────────────────────────── */
  .error-box       { background:#fff5f5; border-left:4px solid #e03535; border-radius:0 8px 8px 0; padding:14px 18px; }
  .error-box-title { font-size:13px; font-weight:700; color:#e03535; margin-bottom:8px; display:flex; align-items:center; }
  .error-list      { margin:0; padding-left:18px; }
  .error-list li   { font-size:13px; color:#c02828; margin-bottom:4px; }
  
  /* ── Form card ───────────────────────────────────────────────────────── */
  .form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
  
  /* ── Sections ────────────────────────────────────────────────────────── */
  .form-section    { padding:24px 28px; }
  .section-label   { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.07em; color:#7a90b0; margin-bottom:20px; }
  .section-divider { border:none; border-top:1px solid #f0f4f8; }
  
  /* ── Fields grid ─────────────────────────────────────────────────────── */
  .fields-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:18px; }
  
  /* ── Form elements ───────────────────────────────────────────────────── */
  .form-group-styled  { display:flex; flex-direction:column; gap:6px; }
  .form-label-styled  { font-size:13px; font-weight:600; color:#1a2b45; }
  .req                { color:#e03535; }
  .form-input-styled  { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s, box-shadow .15s; font-family:inherit; }
  .form-input-styled:focus   { border-color:#004AAD; box-shadow:0 0 0 3px rgba(0,74,173,.08); }
  .form-input-styled:disabled { background:#f8fafc; color:#7a90b0; cursor:not-allowed; }
  .input--error { border-color:#e03535 !important; }
  .input--error:focus { box-shadow:0 0 0 3px rgba(224,53,53,.08) !important; }
  .field-error { font-size:12px; color:#e03535; margin:2px 0 0; }
  
  /* ── Buttons ─────────────────────────────────────────────────────────── */
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 20px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover:not(:disabled) { background:#003b8e; }
  .btn-primary-action:disabled { opacity:.55; cursor:not-allowed; }
  .btn-ghost { display:inline-flex; align-items:center; gap:7px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
  .btn-ghost:hover { background:#e4eaf2; }
  
  /* ── Form footer ─────────────────────────────────────────────────────── */
  .form-footer { display:flex; justify-content:flex-end; gap:10px; padding:20px 28px; border-top:1px solid #e4eaf2; background:#f8fafc; }
  
  /* ── Responsive ──────────────────────────────────────────────────────── */
  @media (max-width:1024px) { .fields-grid { grid-template-columns:repeat(2,1fr); } }
  @media (max-width:640px)  {
    .fields-grid      { grid-template-columns:1fr; }
    .page-header      { flex-direction:column; }
    .page-title-wrap  { flex-direction:column; align-items:stretch; }
    .page-actions     { width:100%; }
  }
  </style>